/**
 * terra-qyt.13 (U9) — DOM-free contracts for playbook-only authoring mode:
 * a discriminated {@link FlowContext} derived from the route, plus the
 * URL builder both doorways (`/play/:id` and `/playbook/:id`) round-trip
 * through. Pure, no React import — beside `play-flow-step.ts` /
 * `play-flow-coverage.ts`, and covered by the same DOM-free bun harness
 * (`mini-apps/playbooks` has no jsdom/happy-dom setup, see
 * `usage-sidebar-logic.ts`'s own module doc for the same constraint).
 *
 * Two doorways, one editing surface: a Play route (`playId` + a REQUIRED
 * `campaignSfdcId`) and a Playbook route (`playbookId` + an OPTIONAL
 * `seedCampaignSfdcId` — a playbook can be authored with no campaign
 * selected yet). `flowContextKey` intentionally differs between the two
 * kinds even when the id string is identical, because a play and a
 * playbook are different flow-state scopes; `originPlaceKey`
 * (`usage-sidebar-logic.ts`) is the OPPOSITE convention on purpose — it
 * unifies the two doorways onto one usage "place" — see that function's
 * doc for why the two must not be confused.
 */

/** A play context always carries campaignSfdcId; a playbook's seed is optional. */
export type FlowContext =
  | { kind: 'play'; playId: string; campaignSfdcId: string }
  | { kind: 'playbook'; playbookId: string; seedCampaignSfdcId: string | null };

/** Raw route inputs — a `useParams()` + `useSearchParams()` read, untouched. */
export interface FlowContextRouteInput {
  playId?: string;
  playbookId?: string;
  campaignSfdcId?: string;
}

/**
 * Exactly one of `playId`/`playbookId` must be present (non-empty) — an
 * ambiguous or unidentified route throws rather than silently picking a
 * side. An absent/blank `campaignSfdcId` becomes `''` for a play context
 * (matching `PlayFlowShell`'s existing `searchParams.get(...) ?? ''`
 * convention — a missing campaign there is a rendered error band, not a
 * thrown error) and `null` for a playbook's seed — never the `''` sentinel
 * (coding-standards.md's "no hardcoded fallbacks" + this bead's own
 * Do-Not-Create: absent seed is `null`, the query param is OMITTED).
 */
export function flowContextFromRoute(input: FlowContextRouteInput): FlowContext {
  const hasPlayId = typeof input.playId === 'string' && input.playId.length > 0;
  const hasPlaybookId = typeof input.playbookId === 'string' && input.playbookId.length > 0;
  if (hasPlayId === hasPlaybookId) {
    throw new Error(
      'flowContextFromRoute requires exactly one of playId or playbookId, got: ' +
        JSON.stringify(input),
    );
  }
  if (hasPlayId) {
    return { kind: 'play', playId: input.playId as string, campaignSfdcId: input.campaignSfdcId ?? '' };
  }
  const seed = input.campaignSfdcId;
  return {
    kind: 'playbook',
    playbookId: input.playbookId as string,
    seedCampaignSfdcId: seed === undefined || seed === '' ? null : seed,
  };
}

/**
 * A deterministic identity for a {@link FlowContext} — deliberately DIFFERENT
 * between a play and a playbook that share the same id string (unlike
 * `originPlaceKey` in `usage-sidebar-logic.ts`, which unifies the two
 * doorways onto one usage "place" — see that function's doc). Play and
 * Playbook routes reset and scope their own flow state independently.
 */
export function flowContextKey(ctx: FlowContext): string {
  return ctx.kind === 'play' ? `play:${ctx.playId}` : `playbook:${ctx.playbookId}`;
}

/**
 * QA round 7, U2 (item 2) — the flow a Playbooks href would MOUNT, as a
 * `flowContextKey`, or `null` for anything that is not a play/playbook route.
 * The inverse of `flowContextPath` below for the two doorways; a `?`/`#` after
 * the id is tolerated. Used by `useGuardedFlowEntry` to tell "entering a
 * different flow" (which `PlayFlowShell`'s context-change clear wipes the
 * stores for) from "returning to the flow whose drafts survive".
 */
export function flowContextKeyFromHref(href: string): string | null {
  const play = /\/play\/([^/?#]+)/.exec(href);
  if (play?.[1]) return flowContextKey({ kind: 'play', playId: decodeURIComponent(play[1]), campaignSfdcId: '' });
  const playbook = /\/playbook\/([^/?#]+)/.exec(href);
  if (playbook?.[1]) {
    return flowContextKey({ kind: 'playbook', playbookId: decodeURIComponent(playbook[1]), seedCampaignSfdcId: null });
  }
  return null;
}

/**
 * Builds the route for either doorway beneath any caller-provided
 * `basePath` (`useAppBasePath()`'s output — never a hardcoded mount path,
 * per coding-standards.md §1). Every interpolated id/value is
 * `encodeURIComponent`-ed, matching `originUrl`'s existing convention
 * (`usage-sidebar-logic.ts:34-39`) — manual concatenation, not
 * `URLSearchParams` (whose `toString()` would encode a space as `+`, not
 * the `%20` this contract's callers expect). A playbook's `null` seed
 * omits the `campaignSfdcId` param entirely (no empty-string sentinel);
 * any caller-supplied `params` (e.g. `step`, `rung`) are appended after it,
 * in insertion order.
 */
export function flowContextPath(
  basePath: string,
  ctx: FlowContext,
  params?: Record<string, string>,
): string {
  const segment =
    ctx.kind === 'play'
      ? `play/${encodeURIComponent(ctx.playId)}`
      : `playbook/${encodeURIComponent(ctx.playbookId)}`;
  const seed = ctx.kind === 'play' ? ctx.campaignSfdcId : ctx.seedCampaignSfdcId;
  const queryParts: string[] = [];
  if (seed !== null) {
    queryParts.push(`campaignSfdcId=${encodeURIComponent(seed)}`);
  }
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  return `${basePath}/${segment}${query}`;
}
